import { ClickEvent, DataSourceAction, DataSourceExecutionEvent, Label, TableRow, TableRowDisplayEvent } from "@mcleod/components";
import { AutogenLayoutPartnerProfileManagementTable } from "./autogen/AutogenLayoutPartnerProfileManagementTable";
import { getFilteredRowCount, toggleExpandablePanel, updateXrefLabelFormatting } from "./DataFusionUtil";
import { CommonTooltips } from "@mcleod/common";
import { ModelRow } from "@mcleod/core";
import { ModelEdiCustomer } from "./models/ModelEdiCustomer";
import { ModelEdiCarrier } from "./models/ModelEdiCarrier";

export class PartnerProfileManagementTable extends AutogenLayoutPartnerProfileManagementTable {
    private _panelExpanded: boolean = false;
    private labelCount: Label;
    private _direction: string;
    private _partnerType: string;
    private _layoutTitle: string = "Profile";
    private _showCustomerColumn: boolean;
    private _showCarrierColumn: boolean;
    private INBOUND = "I";
    private OUTBOUND = "O";
    private _initialLoad: boolean = true;


    override async onLoad() {
        await super.onLoad();
        this.tableProfiles.doOnResolveRowEdit = async (updatedRow: ModelRow) => {
            return await this.doOnResolveRowEdit(updatedRow);
        };
    }

    private createCountLabel() {
        // Create the count label
        this.labelCount = new Label({
            text: "",
            color: "primary",
            fontBold: true,
            fontSize: "large",
            marginLeft: 8,
            rowBreak: false,
            marginBottom: 0,
            paddingBottom: 0
        });

        // Find the parent container of the main label
        const labelParent = this.labelTitle.parent;
        if (labelParent && this.horizontalspacer1) {
            // Insert the count label between the main label and the horizontal spacer
            const spacerIndex = labelParent.indexOf(this.horizontalspacer1);
            if (spacerIndex >= 0) {
                labelParent.insert(this.labelCount, spacerIndex);
            } else {
                // Fallback: add after the main label
                const labelIndex = labelParent.indexOf(this.labelTitle);
                if (labelIndex >= 0) {
                    labelParent.insert(this.labelCount, labelIndex + 1);
                }
            }
        }
    }

    /** This is an event handler for the onClick event of buttonExpand. */
    async buttonExpandOnClick(event: ClickEvent) {
        this._panelExpanded = await toggleExpandablePanel(
            this._panelExpanded,
            this.panelCollapsible,
            this.buttonExpand
        );
    }

    private updateLabelFormatting(forceCollapse: boolean = false) {

        const rowCount = getFilteredRowCount(this.tableProfiles);

        // Set the main label text without count
        this.labelTitle.text = this.layoutTitle;

        // Set the count label text and visibility
        const values = rowCount > 0;
        if (this.labelCount) {
            this.labelCount.text = values ? rowCount.toString() : "";
            this.labelCount.visible = values;
        }
        updateXrefLabelFormatting([
            {
                table: this.tableProfiles,
                label: this.labelTitle,
                isExpanded: this._panelExpanded,
                collapsiblePanel: this.panelCollapsible,
                expandButton: this.buttonExpand,
                onStateChange: (newState: boolean) => {
                    this._panelExpanded = newState;
                }
            }
        ], forceCollapse);

        if (values && !this.initialLoad) {
            this.expandPanel();
        } else {
            this.collapsePanel();
        }
    }

    /** This is an event handler for the afterExecution event of source. */
    sourceAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            // Use setTimeout to ensure the formatting happens after the data is fully loaded
            setTimeout(() => {
                this.updateLabelFormatting();
                this._initialLoad = false;
            }, 100);
        }
    }

    setTableValues(direction: string, partnerType: string, layoutTitle: string, detailLayout: string = null) {
        this.direction = direction;
        this.partnerType = partnerType;
        this.layoutTitle = layoutTitle;
        this.tableProfiles.detailLayout = detailLayout;
        this.tableProfiles.addLayout = detailLayout;
        this.tableProfiles.editLayout = detailLayout;
        this.tableProfiles.generalLayout = detailLayout;
        this.tableProfiles.searchLayout = detailLayout;
        this.tableProfiles.customFilter = (row) => {
            return row.data.get("direction") === this.direction && row.data.get("profile_type") === this.partnerType;
        };

        // Control Customer column visibility based on profile types
        // Show only for inbound order, outbound status, and outbound billing profiles
        this.showCustomerColumn = (direction === this.INBOUND && partnerType === "ediorder_profile") ||
            (direction === this.OUTBOUND && (partnerType === "edistatus_profile" || partnerType === "edibilling_profile"));

        // Access the Customer column by caption and control its visibility
        const customerColumn = this.tableProfiles.getColumnByCaption("Customer");
        if (customerColumn) {
            this.tableProfiles.setColumnVisibility(customerColumn, this.showCustomerColumn);
        }

        // Control Carrier column visibility based on profile types
        // Show only for outbound order, inbound status, and inbound billing profiles
        this.showCarrierColumn = (direction === this.OUTBOUND && partnerType === "ediorder_profile") ||
            (direction === this.INBOUND && (partnerType === "edistatus_profile" || partnerType === "edibilling_profile"));

        // Access the Carrier column by caption and control its visibility
        const carrierColumn = this.tableProfiles.getColumnByCaption("Carrier");
        if (carrierColumn) {
            this.tableProfiles.setColumnVisibility(carrierColumn, this.showCarrierColumn);
        }


        this.tableProfiles.setColumnVisibility(this.tableProfiles.getColumnByCaption("Transaction Type"), partnerType === "edi_profile");


        // Create and add the count label next to the main label
        this.createCountLabel();

        // Set initial label formatting and collapse panels by default
        this.updateLabelFormatting(true);
    }

    async collapsePanel() {
        if (this._panelExpanded) {
            await this.panelCollapsible?.collapse();
            this.buttonExpand.imageRotation = 90;
            this._panelExpanded = !this._panelExpanded;
        }
    }

    async expandPanel() {
        if (!this._panelExpanded) {
            await this.panelCollapsible?.expand();
            this.buttonExpand.imageRotation = 0;
            this._panelExpanded = !this._panelExpanded;
        }
    }


    onTableRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        const modelRow = tableRow.data;

        // Setup tooltips for EDI-related fields
        this.setupTooltip(tableRow, "labelTemplateId", modelRow.get("template_id"), "lme/datafusion/EdiTemplateQuickInfo");
        this.setupTooltip(tableRow, "labelEdiCommId", modelRow.get("edi_comm_id"), "lme/datafusion/EdiCommControlQuickInfo");
    }

    async doOnResolveRowEdit(updatedRow: ModelRow) {
        // Access the parent PartnerProfileManagement layout's main data source
        const parentLayout = this.owner; // Should be PartnerProfileManagement
        if (parentLayout && parentLayout.mainDataSource) {
            // Create a new model row using the main data source's model constructor
            const mainDataSourceModel = parentLayout.mainDataSource;
            if (updatedRow) {
                const transformedRow = new ModelRow(mainDataSourceModel.url, false, {
                    id: updatedRow.get("id"),
                    partner_id: updatedRow.get("partner_id"),
                    alt_partner_id: updatedRow.get("alt_partner_id"),
                    company_id: updatedRow.get("company_id"),
                    partner_name: updatedRow.get("partner_name"),
                    intercompany: updatedRow.get("intercompany"),
                    version: updatedRow.get("version"),
                    direction: updatedRow.get("direction"),
                    profile_type: this.partnerType,
                    template_id: updatedRow.get("template_id"),
                    next_batch: updatedRow.get("next_batch"),
                    edi_comm_id: updatedRow.get("edi_comm_id"),
                    partner_status: updatedRow.get("partner_status"),
                    process_priority: String(updatedRow.get("process_priority")),
                    transaction_set: updatedRow.get("transaction_set"),
                });
                //transformedRow.set("customer_id", updatedRow.get("customer_id"));

                if (this.showCustomerColumn) {
                    const customerIds = await this.setUpCustomer(transformedRow);
                    // Set customer_id field with the comma-delimited string
                    transformedRow.set("customer_id" as any, customerIds);
                }
                if (this.showCarrierColumn) {
                    const carrierIds = await this.setupCarrier(transformedRow);
                    // Set carrier_id field with the comma-delimited string
                    transformedRow.set("carrier_id" as any, carrierIds);
                }
                return transformedRow;
            }

        }
        // Fallback: return the original updated row if transformation fails
        return updatedRow;
    }

    async setUpCustomer(transformedRow: ModelRow): Promise<string> {
        const ediCustomers = await new ModelEdiCustomer().search({
            partner_id: transformedRow.get("partner_id"),
            alt_partner_id: transformedRow.get("alt_partner_id"),
            version: transformedRow.get("version"),
            transaction_set: transformedRow.get("transaction_set"),
            direction: this.direction,
            partner_name: transformedRow.get("partner_name")
        });

        // If there are values in ediCustomers, return comma-delimited customer IDs
        if (ediCustomers?.modelRows?.length > 0) {
            const customerIds = ediCustomers.modelRows
                .map(row => row.get("customer_id"))
                .filter(id => id !== null && id !== undefined && id !== "")
                .join(",");
            return customerIds;
        }

        return "";
    }

    async setupCarrier(transformedRow: ModelRow) {
        const ediCarriers = await new ModelEdiCarrier().search({
            partner_id: transformedRow.get("partner_id"),
            alt_partner_id: transformedRow.get("alt_partner_id"),
            version: transformedRow.get("version"),
            transaction_set: transformedRow.get("transaction_set"),
            direction: this.direction
        });

        // If there are values in ediCarriers, return comma-delimited carrier IDs
        if (ediCarriers?.modelRows?.length > 0) {
            const carrierIds = ediCarriers.modelRows
                .map(row => row.get("carrier_id"))
                .filter(id => id !== null && id !== undefined && id !== "")
                .join(",");
            return carrierIds;
        }

        return "";
    }

    private setupTooltip(tableRow: TableRow, componentId: string, fieldValue: any, layoutPath: string) {
        const component = tableRow.findComponentById(componentId) as Label;
        if (component && fieldValue) {
            CommonTooltips.setTooltipContentLayout(component, fieldValue, layoutPath);
        }
    }

    get direction(): string {
        return this._direction;
    }

    set direction(value: string) {
        this._direction = value;
    }

    get partnerType(): string {
        return this._partnerType;
    }

    set partnerType(value: string) {
        this._partnerType = value;
    }

    get layoutTitle(): string {
        return this._layoutTitle;
    }

    set layoutTitle(value: string) {
        this._layoutTitle = value;
    }

    get initialLoad(): boolean {
        return this._initialLoad;
    }

    set initialLoad(value: boolean) {
        this._initialLoad = value;
    }

    get showCustomerColumn(): boolean {
        return this._showCustomerColumn;
    }

    set showCustomerColumn(value: boolean) {
        this._showCustomerColumn = value;
    }

    get showCarrierColumn(): boolean {
        return this._showCarrierColumn;
    }

    set showCarrierColumn(value: boolean) {
        this._showCarrierColumn = value;
    }

/** This is an event handler for the onClick event of panelHeader. */
  async panelHeaderOnClick(event: ClickEvent) {
    // Only toggle if not clicking the expand button (it has its own handler)
    const target = event.target;

    if (target && target.id === 'buttonExpand') {
      // Let the expand button handle its own click
      return;
    }

    // Toggle panel for header area clicks (title, spacer, etc.)
    this._panelExpanded = await toggleExpandablePanel(
      this._panelExpanded,
      this.panelCollapsible,
      this.buttonExpand
    );
  }
}
